import { Point } from 'pixi.js';
import { GridSystem } from '@gameplay/building/GridSystem';
import { RoomManager } from '@gameplay/management/rooms/RoomManager';
import { RoomCategory } from '@gameplay/management/rooms/RoomTypes';
import { PathfindingSystem } from './PathfindingSystem';

export type ResidentActivity = 
  | 'idle' 
  | 'moving' 
  | 'eating' 
  | 'sleeping' 
  | 'recreation' 
  | 'medical_care'
  | 'bathroom';

export type ResidentPersonality = 
  | 'social' 
  | 'introverted' 
  | 'active' 
  | 'bookworm' 
  | 'complainer';

export interface ResidentNeeds {
  hunger: number;      // 0-100, decreases over time, satisfied by dining
  rest: number;        // 0-100, decreases over time, satisfied by bedroom
  social: number;      // 0-100, satisfied by recreation rooms and other residents
  medical: number;     // 0-100, satisfied by medical rooms
  bathroom: number;    // 0-100, urgent need that must be satisfied
  happiness: number;   // 0-100, affected by room quality and fulfilled needs
}

export interface ResidentStats {
  id: string;
  name: string;
  age: number;
  personality: ResidentPersonality;
  moveSpeed: number;        // tiles per second
  careLevel: number;        // 1-5, affects medical needs and mobility
  currentRoom: string | null;
  assignedBedroom: string | null;
}

export class Resident {
  public readonly stats: ResidentStats;
  public needs: ResidentNeeds;
  public activity: ResidentActivity = 'idle';
  public position: Point;
  public targetPosition: Point | null = null;
  public path: Point[] = [];
  public pathIndex: number = 0;
  
  private readonly gridSystem: GridSystem;
  private readonly roomManager: RoomManager;
  private pathfindingSystem: PathfindingSystem | null = null;
  private lastNeedUpdate: number = 0;
  private activityStartTime: number = 0;

  constructor(
    stats: ResidentStats,
    initialPosition: Point,
    gridSystem: GridSystem,
    roomManager: RoomManager
  ) {
    this.stats = stats;
    this.position = new Point(initialPosition.x, initialPosition.y);
    this.gridSystem = gridSystem;
    this.roomManager = roomManager;
    
    // Initialize needs based on care level and personality
    this.needs = this.generateInitialNeeds();
  }

  public setPathfindingSystem(pathfinding: PathfindingSystem): void {
    this.pathfindingSystem = pathfinding;
  }

  private generateInitialNeeds(): ResidentNeeds {
    return {
      hunger: 50 + Math.random() * 30,
      rest: 60 + Math.random() * 20,
      social: this.stats.personality === 'social' ? 30 : 70,
      medical: Math.max(0, 100 - (this.stats.careLevel * 15)),
      bathroom: 80 + Math.random() * 20,
      happiness: 75
    };
  }

  public update(deltaTime: number): void {
    this.updateNeeds(deltaTime);
    this.updateMovement(deltaTime);
    this.updateActivity(deltaTime);
  }

  private updateNeeds(deltaTime: number): void {
    const now = Date.now();
    if (now - this.lastNeedUpdate < 1000) return; // Update once per second

    // Decay needs over time
    this.needs.hunger = Math.max(0, this.needs.hunger - (deltaTime * 0.8));
    this.needs.rest = Math.max(0, this.needs.rest - (deltaTime * 0.5));
    this.needs.bathroom = Math.max(0, this.needs.bathroom - (deltaTime * 1.2));
    
    // Social need decay depends on personality
    const socialDecay = this.stats.personality === 'social' ? 1.0 : 0.3;
    this.needs.social = Math.max(0, this.needs.social - (deltaTime * socialDecay));
    
    // Medical need increases with age and low care level
    if (this.stats.careLevel > 3) {
      this.needs.medical = Math.max(0, this.needs.medical - (deltaTime * 0.2));
    }

    this.updateHappiness();
    this.lastNeedUpdate = now;
  }

  private updateHappiness(): void {
    // Happiness is affected by fulfilled needs and room quality
    let happiness = 50; // Base happiness
    
    // Need satisfaction affects happiness
    const needsScore = (
      this.needs.hunger + 
      this.needs.rest + 
      this.needs.social + 
      this.needs.medical + 
      this.needs.bathroom
    ) / 5;
    
    happiness += (needsScore - 50) * 0.5;
    
    // Room quality affects happiness
    if (this.stats.currentRoom) {
      const room = this.roomManager.getRoom(this.stats.currentRoom);
      if (room) {
        happiness += (room.quality - 50) * 0.3;
      }
    }
    
    this.needs.happiness = Math.max(0, Math.min(100, happiness));
  }

  private updateMovement(deltaTime: number): void {
    if (this.activity !== 'moving' || this.path.length === 0) return;

    const target = this.path[this.pathIndex];
    if (!target) return;

    const dx = target.x - this.position.x;
    const dy = target.y - this.position.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    if (distance < 0.1) {
      // Reached waypoint
      this.pathIndex++;
      if (this.pathIndex >= this.path.length) {
        // Reached destination
        this.activity = 'idle';
        this.path = [];
        this.pathIndex = 0;
        this.targetPosition = null;
        this.onReachedDestination();
      }
      return;
    }

    // Move towards target
    const moveDistance = this.stats.moveSpeed * deltaTime;
    const moveX = (dx / distance) * moveDistance;
    const moveY = (dy / distance) * moveDistance;
    
    this.position.x += moveX;
    this.position.y += moveY;

    // Update current room
    this.updateCurrentRoom();
  }

  private updateCurrentRoom(): void {
    const room = this.findRoomAtPosition(this.position);
    this.stats.currentRoom = room ? room.id : null;
  }

  private findRoomAtPosition(worldPos: Point): any | null {
    const gridPos = this.worldToGrid(worldPos);
    
    // First check the exact grid position
    let room = this.roomManager.getRoomAt(gridPos.x, gridPos.y, 0);
    if (room) return room;
    
    // If not found at exact position, check neighboring positions
    // This accounts for residents being between grid positions or having slight positioning errors
    const offsets = [
      { x: 0, y: 0 },   // Current position (already checked above)
      { x: 1, y: 0 },   // Right
      { x: -1, y: 0 },  // Left  
      { x: 0, y: 1 },   // Down
      { x: 0, y: -1 },  // Up
      { x: 1, y: 1 },   // Down-right
      { x: -1, y: 1 },  // Down-left
      { x: 1, y: -1 },  // Up-right
      { x: -1, y: -1 }  // Up-left
    ];
    
    for (const offset of offsets) {
      const checkX = gridPos.x + offset.x;
      const checkY = gridPos.y + offset.y;
      room = this.roomManager.getRoomAt(checkX, checkY, 0);
      if (room) {
        // Additional check: make sure the resident is actually close to this room's area
        if (this.isWithinRoomBounds(worldPos, room)) {
          return room;
        }
      }
    }
    
    return null;
  }

  private isWithinRoomBounds(worldPos: Point, room: any): boolean {
    if (!room || !room.tiles || room.tiles.length === 0) return false;
    
    // Get room boundaries
    const bounds = this.getRoomBounds(room);
    const gridPos = this.worldToGrid(worldPos);
    
    // Allow some tolerance (0.5 grid units) for being "within" the room
    const tolerance = 0.5;
    return gridPos.x >= bounds.minX - tolerance && 
           gridPos.x <= bounds.maxX + tolerance &&
           gridPos.y >= bounds.minY - tolerance && 
           gridPos.y <= bounds.maxY + tolerance;
  }

  private getRoomBounds(room: any): {minX: number, maxX: number, minY: number, maxY: number} {
    if (!room.tiles || room.tiles.length === 0) {
      return { minX: 0, maxX: 0, minY: 0, maxY: 0 };
    }
    
    let minX = room.tiles[0].gridX;
    let maxX = room.tiles[0].gridX; 
    let minY = room.tiles[0].gridY;
    let maxY = room.tiles[0].gridY;
    
    for (const tile of room.tiles) {
      minX = Math.min(minX, tile.gridX);
      maxX = Math.max(maxX, tile.gridX);
      minY = Math.min(minY, tile.gridY);
      maxY = Math.max(maxY, tile.gridY);
    }
    
    return { minX, maxX, minY, maxY };
  }

  private updateActivity(deltaTime: number): void {
    const now = Date.now();
    
    // Activity duration checks
    if (this.activity !== 'idle' && this.activity !== 'moving') {
      const activityDuration = now - this.activityStartTime;
      
      // Complete activities after certain durations
      if (this.shouldCompleteActivity(activityDuration)) {
        this.completeCurrentActivity();
      }
    }
    
    // Decision making for new activities
    if (this.activity === 'idle') {
      this.decideNextActivity();
    }
  }

  private shouldCompleteActivity(duration: number): boolean {
    const MINUTE = 60 * 1000; // 60 seconds in ms
    
    switch (this.activity) {
      case 'eating': return duration > 2 * MINUTE;
      case 'sleeping': return duration > 8 * MINUTE;
      case 'recreation': return duration > 3 * MINUTE;
      case 'medical_care': return duration > 1.5 * MINUTE;
      case 'bathroom': return duration > 0.5 * MINUTE;
      default: return false;
    }
  }

  private completeCurrentActivity(): void {
    switch (this.activity) {
      case 'eating':
        this.needs.hunger = Math.min(100, this.needs.hunger + 40);
        break;
      case 'sleeping':
        this.needs.rest = Math.min(100, this.needs.rest + 60);
        break;
      case 'recreation':
        this.needs.social = Math.min(100, this.needs.social + 30);
        break;
      case 'medical_care':
        this.needs.medical = Math.min(100, this.needs.medical + 50);
        break;
      case 'bathroom':
        this.needs.bathroom = Math.min(100, this.needs.bathroom + 80);
        break;
    }
    
    this.activity = 'idle';
  }

  private decideNextActivity(): void {
    // Prioritize urgent needs
    if (this.needs.bathroom < 20) {
      this.seekRoom('bathroom');
      return;
    }
    
    if (this.needs.hunger < 30) {
      this.seekRoom('dining');
      return;
    }
    
    if (this.needs.medical < 30 && this.stats.careLevel > 3) {
      this.seekRoom('medical');
      return;
    }
    
    if (this.needs.rest < 25) {
      this.seekRoom('bedroom');
      return;
    }
    
    if (this.needs.social < 40) {
      this.seekRoom('recreation');
      return;
    }
  }

  private seekRoom(category: RoomCategory): void {
    const rooms = this.roomManager.getRooms().filter(room => room.category === category);
    
    if (rooms.length === 0) {
      // console.log(`${this.stats.name} cannot find a ${category} room`);
      return;
    }
    
    // Find closest room of this type
    const currentGridPos = this.worldToGrid(this.position);
    let closestRoom = null;
    let closestDistance = Infinity;
    
    for (const room of rooms) {
      if (room.tiles.length === 0) continue;
      
      const roomCenter = this.getRoomCenter(room);
      const distance = this.getDistance(currentGridPos, roomCenter);
      
      if (distance < closestDistance) {
        closestDistance = distance;
        closestRoom = room;
      }
    }
    
    if (closestRoom) {
      this.moveToRoom(closestRoom);
    }
  }

  private moveToRoom(room: any): void {
    const roomTarget = this.findAccessibleRoomTile(room);
    if (roomTarget) {
      this.setDestination(roomTarget);
    } else {
      console.log(`${this.stats.name} cannot find accessible tile in ${room.category} room`);
    }
  }

  private findAccessibleRoomTile(room: any): Point | null {
    const currentGridPos = this.worldToGrid(this.position);
    
    // Strategy 1: Find room entry points (tiles near the perimeter)
    const entryPoints = this.findRoomEntryPoints(room);
    if (entryPoints.length > 0) {
      // Sort by distance from current position
      entryPoints.sort((a, b) => {
        const distA = this.getDistance(currentGridPos, a);
        const distB = this.getDistance(currentGridPos, b);
        return distA - distB;
      });
      
      // Try to pathfind to each entry point, return the first accessible one
      for (const entry of entryPoints.slice(0, 3)) { // Check top 3 closest
        if (this.pathfindingSystem) {
          const startWorld = this.gridToWorld(currentGridPos);
          const entryWorld = this.gridToWorld(entry);
          const path = this.pathfindingSystem.findPath(startWorld, entryWorld, 0);
          if (path.length > 0) {
            return entry;
          }
        } else {
          // Fallback: just use closest entry point
          return entry;
        }
      }
    }
    
    // Strategy 2: Try the geometric center if it's walkable
    const roomCenter = this.getRoomCenter(room);
    if (this.isPositionWalkable(roomCenter)) {
      return roomCenter;
    }
    
    // Strategy 3: Find any walkable tile in the room (original logic as fallback)
    let closestTile = null;
    let closestDistance = Infinity;
    
    for (const tile of room.tiles) {
      const tilePos = new Point(tile.gridX, tile.gridY);
      
      if (this.isPositionWalkable(tilePos)) {
        const distance = this.getDistance(currentGridPos, tilePos);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestTile = tilePos;
        }
      }
    }
    
    return closestTile;
  }

  private findRoomEntryPoints(room: any): Point[] {
    if (!room.tiles || room.tiles.length === 0) return [];
    
    const entryPoints: Point[] = [];
    const roomBounds = this.getRoomBounds(room);
    
    // Find tiles that are on the room's perimeter (likely entry points)
    for (const tile of room.tiles) {
      const tilePos = new Point(tile.gridX, tile.gridY);
      
      // Skip if not walkable
      if (!this.isPositionWalkable(tilePos)) continue;
      
      // Check if this tile is on the perimeter by seeing if it has non-room neighbors
      const isPerimeter = this.isRoomPerimeterTile(tile, room);
      if (isPerimeter) {
        entryPoints.push(tilePos);
      }
    }
    
    // If no perimeter tiles found, include tiles near the edges of the room bounds
    if (entryPoints.length === 0) {
      for (const tile of room.tiles) {
        const tilePos = new Point(tile.gridX, tile.gridY);
        if (!this.isPositionWalkable(tilePos)) continue;
        
        // Check if tile is near room boundary
        const isNearEdge = (
          tile.gridX === roomBounds.minX || tile.gridX === roomBounds.maxX ||
          tile.gridY === roomBounds.minY || tile.gridY === roomBounds.maxY
        );
        
        if (isNearEdge) {
          entryPoints.push(tilePos);
        }
      }
    }
    
    return entryPoints;
  }

  private isRoomPerimeterTile(tile: any, room: any): boolean {
    const neighbors = [
      { x: tile.gridX + 1, y: tile.gridY },     // Right
      { x: tile.gridX - 1, y: tile.gridY },     // Left
      { x: tile.gridX, y: tile.gridY + 1 },     // Down
      { x: tile.gridX, y: tile.gridY - 1 }      // Up
    ];
    
    // A tile is on the perimeter if at least one neighbor is not part of this room
    for (const neighbor of neighbors) {
      const neighborRoom = this.roomManager.getRoomAt(neighbor.x, neighbor.y, 0);
      if (!neighborRoom || neighborRoom.id !== room.id) {
        return true; // Has a neighbor that's not in this room
      }
    }
    
    return false;
  }

  private getRoomCenter(room: any): Point {
    const avgX = room.tiles.reduce((sum: number, tile: any) => sum + tile.gridX, 0) / room.tiles.length;
    const avgY = room.tiles.reduce((sum: number, tile: any) => sum + tile.gridY, 0) / room.tiles.length;
    return new Point(Math.floor(avgX), Math.floor(avgY));
  }

  private isPositionWalkable(gridPos: Point): boolean {
    // Check if there's a floor at this position
    const hasFloor = this.gridSystem.hasFloor(gridPos.x, gridPos.y, 0);
    if (!hasFloor) return false;
    
    // Check if there's furniture blocking this position
    const hasFurniture = this.gridSystem.hasFurniture(gridPos.x, gridPos.y, 0);
    return !hasFurniture;
  }

  private setDestination(gridPos: Point): void {
    const worldPos = this.gridToWorld(gridPos);
    this.targetPosition = worldPos;
    this.path = this.findPath(this.worldToGrid(this.position), gridPos);
    this.pathIndex = 0;
    this.activity = 'moving';
  }

  private findPath(start: Point, end: Point): Point[] {
    // Use the advanced pathfinding system if available
    if (this.pathfindingSystem) {
      const startWorld = this.gridToWorld(start);
      const endWorld = this.gridToWorld(end);
      return this.pathfindingSystem.findPath(startWorld, endWorld, 0);
    }
    
    // Fallback to simple pathfinding if pathfinding system not available
    const path: Point[] = [];
    const worldStart = this.gridToWorld(start);
    const worldEnd = this.gridToWorld(end);
    
    // Add intermediate points for smooth movement
    const steps = Math.max(Math.abs(end.x - start.x), Math.abs(end.y - start.y));
    for (let i = 1; i <= steps; i++) {
      const t = i / steps;
      const x = worldStart.x + (worldEnd.x - worldStart.x) * t;
      const y = worldStart.y + (worldEnd.y - worldStart.y) * t;
      path.push(new Point(x, y));
    }
    
    return path;
  }

  private gridToWorld(gridPos: Point): Point {
    return this.gridSystem.gridToWorld(gridPos.x, gridPos.y);
  }

  private worldToGrid(worldPos: Point): Point {
    return this.gridSystem.worldToGrid(worldPos.x, worldPos.y);
  }

  private getDistance(pos1: Point, pos2: Point): number {
    const dx = pos2.x - pos1.x;
    const dy = pos2.y - pos1.y;
    return Math.sqrt(dx * dx + dy * dy);
  }

  private onReachedDestination(): void {
    // Start appropriate activity based on room type
    if (this.stats.currentRoom) {
      const room = this.roomManager.getRoom(this.stats.currentRoom);
      if (room) {
        this.startRoomActivity(room.category);
      }
    }
  }

  private startRoomActivity(category: RoomCategory): void {
    this.activityStartTime = Date.now();
    
    switch (category) {
      case 'dining':
        this.activity = 'eating';
        break;
      case 'bedroom':
        this.activity = 'sleeping';
        break;
      case 'recreation':
        this.activity = 'recreation';
        break;
      case 'medical':
        this.activity = 'medical_care';
        break;
      case 'bathroom':
        this.activity = 'bathroom';
        break;
      default:
        this.activity = 'idle';
    }
  }
}